import { useEffect, useState, useMemo } from "react";
import { CheckSquare, Filter } from "lucide-react";
import { GenericEntityWorkspacePage } from "./shared/GenericEntityWorkspacePage";
import { Button } from "../primitives/Button";
import { Select } from "../primitives/Select";
import { listTasks, getTask, type TaskInstance, type TaskOpsStatus, type TaskLifecycleStatus, type TaskHealthState } from "../../api/tasks";
import type { EntityWorkspaceBoardConfig, EntityListItem } from "./shared/entityWorkspaceBoardConfigs";
import { TaskDetailView } from "../components/task/TaskDetailView";

export function TasksPage({ userName = "Utilisateur" }: { userName?: string }) {
  const [tasks, setTasks] = useState<TaskInstance[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    status_ops: "" as string,
    status_lifecycle: "" as string,
    health_state: "" as string,
  });

  // Load tasks
  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    listTasks({
      status_ops: filters.status_ops || undefined,
      status_lifecycle: filters.status_lifecycle || undefined,
      health_state: filters.health_state || undefined,
      page_size: 200,
    })
      .then((data) => {
        if (!mounted) return;
        setTasks(data.items || []);
        if (data.items && data.items.length > 0 && !selectedId) {
          setSelectedId(data.items[0].id);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load tasks:", error);
        alert(`Erreur lors du chargement des tâches: ${error.message || error}`);
        setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [filters.status_ops, filters.status_lifecycle, filters.health_state]);

  // Convert tasks to entity list items
  const entityListItems: EntityListItem[] = useMemo(() => {
    return tasks.map((task) => ({
      id: task.id,
      title: `Tâche ${task.ops_family}`,
      subtitle: `Client: ${task.client_org_id.substring(0, 8)}...`,
      info: task.health_state,
      icon: <CheckSquare size={16} strokeWidth={1.5} />,
      status: {
        label:
          task.health_state === "good"
            ? "Bon"
            : task.health_state === "attention"
            ? "Attention"
            : "Critique",
        tone:
          task.health_state === "good"
            ? "success"
            : task.health_state === "attention"
            ? "warning"
            : "danger",
      },
    }));
  }, [tasks]);

  const selectedTask = useMemo(() => {
    return tasks.find((t) => t.id === selectedId);
  }, [tasks, selectedId]);

  const config: EntityWorkspaceBoardConfig = useMemo(
    () => ({
      topbarTitle: "Tâches Opérationnelles",
      entities: tasks.map((t) => ({
        id: t.id,
        header: {
          title: `Tâche ${t.ops_family}`,
          subtitle: `Client: ${t.client_org_id.substring(0, 8)}...`,
        },
      })),
      entityListItems,
      boardSectionId: "detail",
      sections: [
        {
          id: "detail",
          label: "Détails",
          active: true,
        },
      ],
    }),
    [tasks, entityListItems]
  );

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Header with filters */}
        <div className="border-b border-border bg-surface p-4 space-y-4">
          <div className="flex items-center gap-4">
            <Filter size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium">Filtres:</span>
            <Select
              value={filters.status_ops}
              onChange={(e) => setFilters({ ...filters, status_ops: e.target.value })}
              placeholder="Statut opérationnel"
              className="w-48"
            >
              <option value="">Tous</option>
              <option value="not_started">Non démarré</option>
              <option value="in_progress">En cours</option>
              <option value="blocked">Bloqué</option>
              <option value="completed">Terminé</option>
            </Select>
            <Select
              value={filters.status_lifecycle}
              onChange={(e) => setFilters({ ...filters, status_lifecycle: e.target.value })}
              placeholder="Statut cycle de vie"
              className="w-48"
            >
              <option value="">Tous</option>
              <option value="active">Actif</option>
              <option value="paused">En pause</option>
              <option value="archived">Archivé</option>
              <option value="cancelled">Annulé</option>
            </Select>
            <Select
              value={filters.health_state}
              onChange={(e) => setFilters({ ...filters, health_state: e.target.value })}
              placeholder="État de santé"
              className="w-48"
            >
              <option value="">Tous</option>
              <option value="good">Bon</option>
              <option value="attention">Attention</option>
              <option value="critical">Critique</option>
            </Select>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-hidden">
          <GenericEntityWorkspacePage
            config={config}
            userName={userName}
            items={entityListItems}
            selectedId={selectedId}
            onSelect={setSelectedId}
            customContent={
              selectedTask ? (
                <TaskDetailView taskId={selectedTask.id} />
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  Sélectionnez une tâche pour voir les détails
                </div>
              )
            }
          />
        </div>
      </div>
    </>
  );
}
